/**
 * Node Modules
 */
import React, { createElement } from 'react'
import { Helmet } from 'react-helmet'
import { graphql } from 'gatsby'
import delve from 'dlv'
import marksy from 'marksy/jsx'

import './style.css'

/**
 * Application Modules
 */
import PageContainer from '../../components/page-container'
import Footer from '../../components/footer'
import Header from '../../components/header'
import Section from '../../components/section'
import DownloadBanner from '../../components/download-banner'

function br() {
  return <br />
}

const compile = marksy({
  createElement,

  // You can override the default elements with
  // custom VDOM trees
  elements: {
    a({ href, title, target, children }) {
      return (
        <a target={target || '_blank'} rel="noreferrer" href={href}>
          {children}
        </a>
      )
    },
  },

  components: {
    br: br
  },
})


function Article({article, title, publishedDate}) {
  return (
    <Section className="blog-article blog-article--white">
      <div className="blog-article__grid-meta grid">
        <div className="blog-article__details blog-article__details--grey column-start-1 column-start-2--t column-start-3--d column-end-6 column-end-11--t column-end-10--d">
          Press Release — {publishedDate}
        </div>
        <h1 className="blog-article__title blog-article__title--purple column-start-1 column-start-2--t column-start-3--d column-end-6 column-end-11--t column-end-10--d">
          {title}
        </h1>
      </div>
      <div className="blog-article__grid-article grid">
        <article className="blog-article__article column-start-1 column-start-2--t column-start-3--d column-end-6 column-end-11--t column-end-10--d">
          {compile(article).tree}
        </article>
      </div>
    </Section>
  )
}

const styles = {
  '--logo-color': 'var(--purple)',
  '--color': 'var(--light-purple)',
  '--active-color': 'var(--coral)',
  '--background-color': 'var(--white)',
}

function PressReleaseTemplate({ data }) {
  const { publishedDate, title, urlSlug } = data.contentfulContentPressRelease
  const article = delve(data, 'contentfulContentPressRelease.article.article', false)

  const { referencePageMetaData } = data.contentfulContentPressRelease
  const {
    pageTitle,
    description,
    ogDescription,
    ogTitle,
    ogImage,
    twitterDescription,
    twitterImage,
    twitterTitle,
  } = referencePageMetaData

  const ogImageUrl = delve(ogImage, 'file.url', null)
  const twitterImageUrl = delve(twitterImage, 'file.url', null)

  return (
    <PageContainer>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Kinzoo | {pageTitle || ogTitle || twitterTitle || title}</title>
        <meta name="description" content={description || ogDescription || twitterDescription} />
        <meta property="og:type" content="website" />
        <link rel="shortcut icon" href="/favicon.png"></link>
        <link
          rel="canonical"
          href={`https://kinzoo.com/press-release/${urlSlug}/`}
        />

        <meta property="og:title" content={ogTitle || pageTitle || twitterTitle || title} />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content={`https://kinzoo.com/press-release/${urlSlug}/`}
        />
        <meta
          property="og:description"
          content={ogDescription || description || twitterDescription}
        />
        <meta
          name="image"
          property="og:image"
          content={`https:${ogImageUrl ||
            twitterImageUrl}?w=3000&h=1570&fit=fill&f=faces&fm=jpg&q=82`}
        />
        <meta name="twitter:site" content="@kinzoomessenger" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:creator" content="@kinzoomessenger" />
        <meta name="twitter:title" content={twitterTitle || ogTitle || pageTitle || title} />
        <meta
          name="twitter:description"
          content={twitterDescription || ogDescription || description}
        />
        <meta
          name="twitter:image"
          content={`https:${twitterImageUrl ||
            ogImageUrl}?w=3000&h=1570&fit=fill&f=faces&fm=jpg&q=82`}
        />
      </Helmet>
      <Header styles={styles} />
      <Article
        url={`https://kinzoo.com/press-release/${urlSlug}/`}
        publishedDate={publishedDate}
        title={title}
        article={article}
      />
      <DownloadBanner />
      <Footer />
    </PageContainer>
  )
}

export default PressReleaseTemplate

export const pageQuery = graphql`
  query PressReleaseQuery($slug: String!) {
    contentfulContentPressRelease(urlSlug: { eq: $slug }) {
      referencePageMetaData {
        pageTitle
        description
        keywords
        ogDescription
        ogTitle
        ogType
        ogImage {
          file {
            url
          }
        }

        pageTitle
        twitterCard
        twitterCreator
        twitterDescription
        twitterTitle
        twitterImage {
          file {
            url
          }
        }
      }
      article {
        id
        article
      }
      urlSlug
      title
      publishedDate(formatString: "MMM DD, YYYY")
    }
  }
`
